import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  trackOfferDetailClick,
  trackPricingCtaClick,
  trackPricingFaqInteract,
  trackPricingPageView,
  trackServiceCardInteract,
  observeServiceCardVisibility,
  trackServiceGuideSelect,
  trackLinkClick,
} from '../lib/analytics';
import BRAND from '../config/brand';
import {
  COMPARISON_ROWS,
  GOAL_SETTING_MEETING,
  OFFERS,
  ONGOING_MEETINGS,
  PRICES,
  PRIMARY_WAYS,
  PRICING_FAQS,
  SERVICE_GUIDE_OPTIONS,
  SERVICES_PATH,
  buildIntakePath,
  buildMeetingPath,
  getOfferById,
} from '../config/planningOffers';
import PublicSiteNav from './PublicSiteNav';
import './PublicLinksPage.css';

const planningAreas = [
  'Reduce taxes through proactive planning',
  'Evaluate home purchase affordability',
  'Compare job offers and compensation changes',
  'Coordinate equity compensation decisions',
  'Build retirement projections and savings targets',
  'Organize cash flow and major expenses',
  'Improve investment allocation decisions',
  'Coordinate estate and insurance planning',
];

const painPoints = [
  'Too busy to coordinate every financial decision',
  'Uncertainty around home affordability',
  'Job changes that affect taxes and cash flow',
  'Equity compensation that is hard to evaluate',
  'Retirement uncertainty despite strong income',
  'Reactive instead of proactive tax planning',
  'Multiple financial decisions happening at once',
];

const servicePillars = [
  ['bar-chart', 'Data-driven analysis', 'We bring structure and clarity to complex financial decisions.'],
  ['workflow', 'Scenario modeling', 'We model options so you can understand the tradeoffs.'],
  ['receipt', 'Tax-aware planning', 'We identify opportunities to reduce taxes and increase after-tax results.'],
  ['file-text', 'Automation & reporting', 'We build systems that save time and keep your plan organized.'],
  ['refresh', 'Ongoing accountability', 'We review progress and adjust the plan as life evolves.'],
];

const sampleAnalyses = [
  {
    title: 'Can we comfortably afford a $750k-$1M home?',
    description:
      'Compare mortgage scenarios, taxes, cash flow, liquidity targets, and long-term affordability before committing.',
    caption: 'Compare monthly cash flow and surplus under different home price scenarios.',
    chartType: 'bars',
  },
  {
    title: 'Which accounts should we fund first: Roth, Traditional, or Taxable?',
    description:
      'Compare Roth, Traditional, and taxable account funding strategies based on taxes, retirement goals, income level, and long-term flexibility.',
    caption: 'See how account funding choices affect taxes, liquidity, and long-term retirement outcomes.',
    chartType: 'account-funding',
  },
  {
    title: 'How would a compensation change affect our finances?',
    description:
      'Estimate how salary increases, bonuses, RSUs, taxes, benefits, and retirement contributions affect take-home pay and long-term planning.',
    caption:
      'Compare current compensation against a new offer after taxes, benefits, retirement contributions, and cash-flow changes.',
    chartType: 'compensation',
  },
];

const monthlyReports = [
  'Budgeting progress',
  'Net worth changes',
  'Investment updates',
  'Retirement progress',
];

function PlanningIcon({ type }) {
  const common = {
    width: '38',
    height: '38',
    viewBox: '0 0 38 38',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': 'true',
  };

  if (type === 'bar-chart') {
    return (
      <svg {...common}>
        <path d="M7 31V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M7 31H32" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <rect x="11" y="20" width="4.5" height="7" rx="1" stroke="currentColor" strokeWidth="2" />
        <rect x="18" y="14" width="4.5" height="13" rx="1" stroke="currentColor" strokeWidth="2" />
        <rect x="25" y="8" width="4.5" height="19" rx="1" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
  }

  if (type === 'workflow') {
    return (
      <svg {...common}>
        <circle cx="10" cy="10" r="3.5" stroke="currentColor" strokeWidth="2" />
        <circle cx="28" cy="10" r="3.5" stroke="currentColor" strokeWidth="2" />
        <circle cx="19" cy="28" r="3.5" stroke="currentColor" strokeWidth="2" />
        <path d="M13.5 10H24.5M12 13L17 24M26 13L21 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'receipt') {
    return (
      <svg {...common}>
        <path d="M11 7H27V31L23.5 28.5L20 31L16.5 28.5L13 31L11 29.5V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M15 14H23M15 19H23M15 24H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === 'file-text') {
    return (
      <svg {...common}>
        <path d="M12 7H23L28 12V31H12V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M23 7V13H28" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
        <path d="M16 18H24M16 23H24M16 27H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M29 14A10 10 0 0 0 11 10L8 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 8V13H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9 24A10 10 0 0 0 27 28L30 25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M30 30V25H25" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AnalysisChart({ type }) {
  if (type === 'bars') {
    return (
      <svg className="public-service-analysis-svg public-service-analysis-svg--budget" viewBox="0 0 320 240" role="img" aria-label="Stacked monthly budget comparison for today, 750k home, and 850k home scenarios">
        <rect width="320" height="240" rx="10" fill="#fbfdff" />
        <path d="M30 54H290M30 83H290M30 112H290M30 141H290M30 170H290" stroke="#e6edf5" strokeWidth="1" />
        <text x="36" y="25" className="chart-title">Monthly budget scenarios</text>

        <g aria-label="Today has the most monthly flexibility">
          <rect x="46" y="152" width="58" height="18" fill="#ef476f" />
          <rect x="46" y="140" width="58" height="12" fill="#ff7a45" />
          <rect x="46" y="118" width="58" height="22" fill="#103d70" />
          <rect x="46" y="68" width="58" height="50" fill="#8a949b" />
          <rect x="46" y="50" width="58" height="18" rx="4" fill="#3fbf72" />
          <text x="75" y="190" textAnchor="middle" className="chart-label">Today</text>
        </g>

        <g aria-label="750k home creates tighter monthly cash flow">
          <rect x="131" y="153" width="58" height="17" fill="#ef476f" />
          <rect x="131" y="142" width="58" height="11" fill="#ff7a45" />
          <rect x="131" y="108" width="58" height="34" fill="#103d70" />
          <rect x="131" y="56" width="58" height="52" fill="#8a949b" />
          <rect x="131" y="49" width="58" height="7" rx="3" fill="#3fbf72" />
          <text x="160" y="190" textAnchor="middle" className="chart-label">$750k</text>
        </g>

        <g aria-label="850k home leaves the least monthly surplus">
          <rect x="216" y="153" width="58" height="17" fill="#ef476f" />
          <rect x="216" y="142" width="58" height="11" fill="#ff7a45" />
          <rect x="216" y="104" width="58" height="38" fill="#103d70" />
          <rect x="216" y="52" width="58" height="52" fill="#8a949b" />
          <rect x="216" y="50" width="58" height="2" rx="1" fill="#3fbf72" />
          <text x="245" y="190" textAnchor="middle" className="chart-label">$850k</text>
        </g>

        <line x1="30" y1="44" x2="290" y2="44" stroke="#2563eb" strokeWidth="2" strokeDasharray="5 5" />
        <rect x="30" y="27" width="88" height="16" rx="4" fill="#fbfdff" opacity="0.96" />
        <text x="34" y="39" className="chart-income-label">Gross income</text>

        <rect x="30" y="207" width="8" height="8" fill="#ef476f" />
        <text x="42" y="214" className="chart-legend">Federal tax</text>
        <rect x="112" y="207" width="8" height="8" fill="#ff7a45" />
        <text x="124" y="214" className="chart-legend">FICA</text>
        <rect x="164" y="207" width="8" height="8" fill="#103d70" />
        <text x="176" y="214" className="chart-legend">Housing</text>
        <rect x="30" y="223" width="8" height="8" fill="#8a949b" />
        <text x="42" y="230" className="chart-legend">Other spending</text>
        <rect x="164" y="223" width="8" height="8" fill="#3fbf72" />
        <text x="176" y="230" className="chart-legend">Surplus</text>
      </svg>
    );
  }

  if (type === 'account-funding') {
    return (
      <svg className="public-service-analysis-svg" viewBox="0 0 280 170" role="img" aria-label="Three account funding bars for Roth, Traditional, and Taxable">
        <rect width="280" height="170" rx="10" fill="#fbfdff" />
        <path d="M32 36H252M32 70H252M32 104H252M32 138H252" stroke="#e6edf5" strokeWidth="1" />
        <text x="32" y="23" className="chart-title">Funding strategy</text>
        <rect x="48" y="70" width="44" height="68" rx="5" fill="#103d70" />
        <rect x="118" y="50" width="44" height="88" rx="5" fill="#5d8cbd" />
        <rect x="188" y="88" width="44" height="50" rx="5" fill="#a5e2ff" />
        <text x="70" y="156" textAnchor="middle" className="chart-label">Roth</text>
        <text x="140" y="156" textAnchor="middle" className="chart-label">Traditional</text>
        <text x="210" y="156" textAnchor="middle" className="chart-label">Taxable</text>
      </svg>
    );
  }

  if (type === 'compensation') {
    return (
      <svg className="public-service-analysis-svg public-service-analysis-svg--compensation" viewBox="0 0 280 220" role="img" aria-label="Stacked bar comparison of current compensation and new offer">
        <rect width="280" height="220" rx="10" fill="#fbfdff" />
        <path d="M36 44H244M36 78H244M36 112H244M36 146H244" stroke="#e6edf5" strokeWidth="1" />
        <text x="32" y="24" className="chart-title">After-tax comparison</text>

        <rect x="76" y="60" width="50" height="32" rx="5" fill="#a5e2ff" />
        <rect x="76" y="92" width="50" height="58" rx="0" fill="#5d8cbd" />
        <rect x="76" y="150" width="50" height="18" rx="5" fill="#103d70" />

        <rect x="160" y="38" width="50" height="44" rx="5" fill="#a5e2ff" />
        <rect x="160" y="82" width="50" height="66" rx="0" fill="#5d8cbd" />
        <rect x="160" y="148" width="50" height="20" rx="5" fill="#103d70" />

        <text x="101" y="184" textAnchor="middle" className="chart-label">Current</text>
        <text x="185" y="184" textAnchor="middle" className="chart-label">New Offer</text>

        <circle cx="50" cy="205" r="4" fill="#a5e2ff" />
        <text x="60" y="209" className="chart-legend">Taxes</text>
        <circle cx="114" cy="205" r="4" fill="#5d8cbd" />
        <text x="124" y="209" className="chart-legend">Take-home</text>
        <circle cx="204" cy="205" r="4" fill="#103d70" />
        <text x="214" y="209" className="chart-legend">Benefits</text>
      </svg>
    );
  }

  return null;
}

function offerIntakePath(offer, promotionalAngle) {
  return buildIntakePath({
    offerId: offer.id,
    pricingModel: offer.pricingModel,
    cta: 'offer_cta',
    promotionalAngle,
  });
}

function OfferCta({ offer, buttonId, promotionalAngle = 'pricing_page', ctaLocation = 'offer_card' }) {
  const path = offerIntakePath(offer, promotionalAngle);
  return (
    <Link
      className="public-service-cta public-service-cta--primary"
      to={path}
      onClick={() =>
        trackPricingCtaClick(path, offer.ctaLabel, {
          button_id: buttonId,
          button_label: offer.ctaLabel,
          selected_offer: offer.id,
          offer_id: offer.id,
          pricing_model: offer.pricingModel,
          promotional_angle: promotionalAngle,
          content_category: 'pricing',
          cta_location: ctaLocation,
        })
      }
    >
      {offer.ctaLabel}
    </Link>
  );
}

function focusAnchorTarget(id) {
  if (!id || typeof document === 'undefined') return;
  const node = document.getElementById(id);
  if (!node) return;
  node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const focusTarget = node.querySelector('h2, h3') || node;
  if (typeof focusTarget.focus === 'function') {
    if (!focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1');
    }
    focusTarget.focus({ preventScroll: true });
  }
}

function OfferDetailCard({ offer }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const node = cardRef.current;
    if (!node) return undefined;
    return observeServiceCardVisibility(node, {
      offer_id: offer.id,
      selected_offer: offer.id,
      pricing_model: offer.pricingModel,
      content_category: 'pricing',
      cta_location: 'offer_card',
    });
  }, [offer.id, offer.pricingModel]);

  return (
    <article
      ref={cardRef}
      id={offer.anchor}
      className={`public-offer-card ${offer.exampleOnly ? 'public-offer-card--example' : ''}`}
      aria-labelledby={`${offer.anchor}-heading`}
    >
      <div className="public-offer-card-head">
        {offer.exampleOnly ? <span className="public-offer-example-badge">Example only</span> : null}
        <h3 id={`${offer.anchor}-heading`} tabIndex={-1}>{offer.title}</h3>
        <p className="public-offer-price">{offer.priceLabel}</p>
        {offer.priceNote ? <p className="public-offer-price-note">{offer.priceNote}</p> : null}
        <p><strong>Best for:</strong> {offer.bestFor}</p>
      </div>
      <ul className="public-offer-includes">
        {offer.includes.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <div className="public-service-actions">
        <OfferCta offer={offer} buttonId={`offer_cta_${offer.id}`} />
      </div>
    </article>
  );
}

function PricingFaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonId = useId();

  const toggle = () => {
    const next = !open;
    setOpen(next);
    trackPricingFaqInteract({
      faq_id: faq.id,
      faq_action: next ? 'open' : 'close',
      content_category: 'pricing',
    });
  };

  return (
    <div className={`public-pricing-faq-item ${open ? 'is-open' : ''}`}>
      <h3>
        <button
          type="button"
          id={buttonId}
          className="public-pricing-faq-button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={toggle}
        >
          <span>{faq.question}</span>
          <span className="public-pricing-faq-icon" aria-hidden="true">{open ? '−' : '+'}</span>
        </button>
      </h3>
      <div
        id={panelId}
        role="region"
        aria-labelledby={buttonId}
        hidden={!open}
        className="public-pricing-faq-panel"
      >
        <p>{faq.answer}</p>
      </div>
    </div>
  );
}

function ServiceSelectionGuide() {
  const [selectedId, setSelectedId] = useState(null);
  const resultHeadingRef = useRef(null);
  const selected = SERVICE_GUIDE_OPTIONS.find((option) => option.id === selectedId) || null;
  const offer = selected ? getOfferById(selected.mapsToOfferId) : null;

  const choose = (option) => {
    setSelectedId(option.id);
    const mapped = getOfferById(option.mapsToOfferId);
    trackServiceGuideSelect({
      guide_choice: option.id,
      selected_offer: option.mapsToOfferId,
      offer_id: option.mapsToOfferId,
      pricing_model: mapped?.pricingModel,
      content_category: 'pricing',
      promotional_angle: 'service_guide',
      cta_location: 'service_guide',
    });
    window.requestAnimationFrame(() => {
      if (resultHeadingRef.current) {
        resultHeadingRef.current.focus({ preventScroll: true });
      }
    });
  };

  return (
    <section
      id="service-guide"
      className="public-linktree-panel public-service-panel"
      aria-labelledby="service-guide-heading"
    >
      <p className="public-linktree-kicker">Service-selection guide</p>
      <h2 id="service-guide-heading" tabIndex={-1}>Not sure which option fits?</h2>
      <p>
        Choose the statement that best matches your situation. This is guidance only—not an automated
        recommendation or a guarantee that one engagement is right for every household.
      </p>
      <div className="public-service-guide-options" role="group" aria-label="Service selection guidance">
        {SERVICE_GUIDE_OPTIONS.map((option) => {
          const isSelected = selectedId === option.id;
          return (
            <button
              key={option.id}
              type="button"
              className={`public-service-guide-option ${isSelected ? 'is-selected' : ''}`}
              aria-pressed={isSelected}
              onClick={() => choose(option)}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      {selected && offer ? (
        <div className="public-service-guide-result" role="status" aria-live="polite">
          <p className="public-service-guide-result-kicker">Suggested starting point</p>
          <h3 ref={resultHeadingRef} tabIndex={-1}>{offer.title}</h3>
          <p>{selected.guidance}</p>
          <p className="public-offer-price">{offer.priceLabel}</p>
          <div className="public-service-actions">
            <a
              className="public-service-cta"
              href={`#${offer.anchor}`}
              onClick={(event) => {
                event.preventDefault();
                trackOfferDetailClick({
                  button_id: `guide_view_${offer.id}`,
                  button_label: `View ${offer.title}`,
                  selected_offer: offer.id,
                  offer_id: offer.id,
                  pricing_model: offer.pricingModel,
                  promotional_angle: 'service_guide',
                  cta_location: 'service_guide',
                });
                focusAnchorTarget(offer.anchor);
              }}
            >
              View offer details
            </a>
            <OfferCta
              offer={offer}
              buttonId={`guide_cta_${offer.id}`}
              promotionalAngle="service_guide"
              ctaLocation="service_guide"
            />
          </div>
        </div>
      ) : (
        <p className="public-service-guide-empty">
          No option is preselected. Pick the statement that sounds closest to your need.
        </p>
      )}
    </section>
  );
}

export default function FinancialPlanningServicesPage() {
  const location = useLocation();

  useEffect(() => {
    document.title = 'Services & Pricing | Bautista Planning and Analytics';
    trackPricingPageView({
      content_category: 'pricing',
      promotional_angle: 'services_pricing_page',
    });
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, '');
    // Defer so sticky nav + layout settle before scrolling/focusing.
    const timer = window.setTimeout(() => focusAnchorTarget(id), 50);
    return () => window.clearTimeout(timer);
  }, [location.hash]);

  const meetingPath = buildMeetingPath({ promotionalAngle: 'services_hero' });

  return (
    <div className="public-linktree-page public-service-page">
      <PublicSiteNav />
      <main className="public-linktree-shell public-service-shell">
        <header className="public-service-header">
          <Link className="public-service-logo" to="/links" aria-label="Back to Bautista Planning links">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" />
          </Link>
          <p className="public-linktree-kicker">Services &amp; Pricing</p>
          <h1>Clear ways to work together—with prices you can see up front.</h1>
          <p>
            Bautista Planning and Analytics helps busy professionals make connected decisions about
            taxes, cash flow, investments, risk, and long-term goals through one comprehensive
            financial plan, with ongoing planning available afterward.
          </p>
          <div className="public-service-actions public-service-actions--centered">
            <a
              className="public-service-cta public-service-cta--primary"
              href="#pricing-comparison"
              onClick={() =>
                trackServiceCardInteract({
                  interaction_type: 'jump_to_comparison',
                  content_category: 'pricing',
                  promotional_angle: 'services_hero',
                })
              }
            >
              Compare services &amp; pricing
            </a>
            <Link
              className="public-service-cta"
              to={meetingPath}
              onClick={() =>
                trackPricingCtaClick(meetingPath, GOAL_SETTING_MEETING.cta, {
                  destination_type: 'internal',
                  offer_id: GOAL_SETTING_MEETING.offerId,
                  button_id: 'financial_planning_hero_cta',
                  button_label: GOAL_SETTING_MEETING.cta,
                  promotional_angle: 'services_hero',
                  content_category: 'pricing',
                  cta_location: 'services_hero',
                })
              }
            >
              {GOAL_SETTING_MEETING.cta}
            </Link>
          </div>
        </header>

        <section
          className="public-linktree-panel public-service-panel"
          aria-labelledby="ways-to-work-heading"
        >
          <p className="public-linktree-kicker">How we work together</p>
          <h2 id="ways-to-work-heading">Create the plan, then keep it current</h2>
          <p>
            Start with a comprehensive plan that coordinates the major parts of your financial life.
            After the plan is complete, ongoing planning can support implementation and keep it current.
          </p>
          <div className="public-ways-grid">
            {PRIMARY_WAYS.map((way) => (
              <article key={way.id} className="public-ways-card">
                <h3>{way.title}</h3>
                <p className="public-offer-price">{way.priceLabel}</p>
                <p>{way.summary}</p>
              </article>
            ))}
          </div>
        </section>

        <section
          id="pricing-comparison"
          className="public-linktree-panel public-service-panel"
          aria-labelledby="pricing-comparison-heading"
        >
          <p className="public-linktree-kicker">At a glance</p>
          <h2 id="pricing-comparison-heading">Comprehensive and ongoing financial planning</h2>
          <p>Start with the plan. Continue with ongoing support when you want help implementing and updating it.</p>
          <div className="public-pricing-compare-wrap">
            <table className="public-pricing-compare">
              <caption className="sr-only">
                Comparison of comprehensive and ongoing financial planning
              </caption>
              <thead>
                <tr>
                  <th scope="col"> </th>
                  <th scope="col">Comprehensive</th>
                  <th scope="col">Ongoing</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON_ROWS.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    <td>{row.comprehensive}</td>
                    <td>{row.ongoing}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="public-linktree-fees public-linktree-fees--extended" aria-label="Financial planning fees">
            <article>
              <span>Comprehensive plan</span>
              <strong>{PRICES.comprehensive}</strong>
            </article>
            <article>
              <span>Ongoing planning</span>
              <strong>{PRICES.ongoingMonthly} / month</strong>
            </article>
          </div>
        </section>

        <section
          id="offers"
          className="public-linktree-panel public-service-panel"
          aria-labelledby="offers-heading"
        >
          <p className="public-linktree-kicker">Offers in detail</p>
          <h2 id="offers-heading">What each service includes</h2>
          <p>
            Deliverables and intended use cases are listed below. Planning does not promise investment
            performance, tax savings, or outcomes that fit every household.
          </p>
          <div className="public-offer-grid">
            {OFFERS.map((offer) => (
              <OfferDetailCard key={offer.id} offer={offer} />
            ))}
          </div>
        </section>

        <section
          id="ongoing-timeline"
          className="public-linktree-panel public-service-panel"
          aria-labelledby="strategy-meetings-heading"
        >
          <p className="public-linktree-kicker">Ongoing planning rhythm</p>
          <h2 id="strategy-meetings-heading">Three strategy meetings each year</h2>
          <p>
            The {PRICES.ongoingMonthly} / month ongoing service includes investment reports, done-for-you budgeting,
            net-worth tracking, and the annual meeting rhythm below.
          </p>
          <ol className="public-ongoing-timeline">
            {ONGOING_MEETINGS.map((meeting, index) => (
              <li key={meeting.id} className="public-ongoing-timeline-item">
                <span className="public-ongoing-timeline-step" aria-hidden="true">{index + 1}</span>
                <article>
                  <span>{meeting.season}</span>
                  <strong>{meeting.title}</strong>
                  <p>{meeting.description}</p>
                </article>
              </li>
            ))}
          </ol>
          <div className="public-service-monthly-reports">
            <h3>Monthly progress reporting</h3>
            <p>
              Each month, ongoing clients receive a concise progress report covering budgeting, net worth
              changes, investment updates, and retirement progress.
            </p>
            <div className="public-service-monthly-grid">
              {monthlyReports.map((item) => (
                <article key={item}>{item}</article>
              ))}
            </div>
          </div>
          {getOfferById('ongoing_planning') ? (
            <div className="public-service-actions" style={{ marginTop: '1.25rem' }}>
              <OfferCta
                offer={getOfferById('ongoing_planning')}
                buttonId="ongoing_timeline_cta"
                promotionalAngle="ongoing_timeline"
                ctaLocation="ongoing_timeline"
              />
            </div>
          ) : null}
        </section>

        <ServiceSelectionGuide />

        <section
          id="pricing-faq"
          className="public-linktree-panel public-service-panel"
          aria-labelledby="pricing-faq-heading"
        >
          <p className="public-linktree-kicker">FAQ</p>
          <h2 id="pricing-faq-heading">Pricing questions</h2>
          <div className="public-pricing-faq-list">
            {PRICING_FAQS.map((faq) => (
              <PricingFaqItem key={faq.id} faq={faq} />
            ))}
          </div>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="high-income-clarity-heading">
          <p className="public-linktree-kicker">The planning problem</p>
          <h2 id="high-income-clarity-heading">High income does not automatically create financial clarity.</h2>
          <p>
            High earners often do not lack income. They lack time, coordination, and a clear way to
            compare decisions before committing. A mistake in taxes, equity compensation, home
            affordability, or retirement planning can cost tens of thousands of dollars.
          </p>
          <p>
            The challenge is that each decision touches the others: job changes affect taxes and cash
            flow, home purchases affect savings capacity, equity compensation affects investment risk,
            and benefits elections affect long-term planning.
          </p>
          <div className="public-service-check-grid">
            {painPoints.map((item) => (
              <div className="public-service-check-item" key={item}>{item}</div>
            ))}
          </div>
        </section>

        <section
          id="planning-process"
          className="public-linktree-panel public-service-panel"
          aria-labelledby="planning-system-heading"
        >
          <p className="public-linktree-kicker">The planning system</p>
          <h2 id="planning-system-heading">Planning turns connected decisions into a coordinated strategy.</h2>
          <p>
            Bautista Planning and Analytics combines financial planning with data analytics and
            automation to help clients make more informed decisions with less stress and less manual effort.
          </p>
          <p>
            This is not just investment management. It is analytical decision support for high earners
            with limited time and increasingly connected financial decisions.
          </p>
          <div className="public-service-pillar-grid">
            {servicePillars.map(([icon, title, description]) => (
              <article className="public-service-pillar" key={title}>
                <PlanningIcon type={icon} />
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="sample-analysis-heading">
          <p className="public-linktree-kicker">Sample planning analysis</p>
          <h2 id="sample-analysis-heading">See how planning decisions get modeled.</h2>
          <p>
            Clients receive planning analyses that organize taxes, cash flow, housing decisions,
            retirement projections, and long-term tradeoffs into one coordinated view.
          </p>
          <div className="public-service-analysis-grid">
            {sampleAnalyses.map((analysis) => (
              <article className="public-service-analysis-card" key={analysis.title}>
                <div className="public-service-analysis-copy">
                  <h3>{analysis.title}</h3>
                  <p>{analysis.description}</p>
                </div>
                <AnalysisChart type={analysis.chartType} />
                <p className="public-service-analysis-caption">{analysis.caption}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="public-linktree-panel public-service-panel" aria-labelledby="planning-help-heading">
          <p className="public-linktree-kicker">Planning can help with</p>
          <h2 id="planning-help-heading">The decisions where clarity can pay for itself.</h2>
          <ul className="public-linktree-list public-service-list">
            {planningAreas.map((area) => (
              <li key={area}>{area}</li>
            ))}
          </ul>
          <p style={{ marginTop: '1rem' }}>
            Estate and insurance topics are planning coordination topics—not legal advice or insurance
            sales. Tax planning here means proactive planning recommendations, not tax preparation.
          </p>
        </section>

        <section className="public-service-final-cta" aria-label="Get a Financial Plan">
          <h2>Make the next financial decision with more confidence.</h2>
          <p>
            Start your comprehensive financial plan and turn connected decisions into one coordinated roadmap.
          </p>
          <div className="public-service-actions public-service-actions--centered">
            <a
              className="public-service-cta"
              href="#service-guide"
              onClick={() =>
                trackLinkClick(`${SERVICES_PATH}#service-guide`, 'Open service guide', {
                  destination_type: 'internal',
                  button_id: 'footer_service_guide',
                  button_label: 'Open service guide',
                  content_category: 'pricing',
                })
              }
            >
              Open the service guide
            </a>
            <Link
              className="public-service-cta public-service-cta--primary"
              to={buildIntakePath({ offerId: 'comprehensive_plan', cta: 'get_financial_plan', promotionalAngle: 'services_footer' })}
              onClick={() => {
                const path = buildIntakePath({ offerId: 'comprehensive_plan', cta: 'get_financial_plan', promotionalAngle: 'services_footer' });
                trackPricingCtaClick(path, 'Get a Financial Plan', {
                  destination_type: 'contact_form',
                  button_id: 'financial_planning_footer_cta',
                  button_label: 'Get a Financial Plan',
                  promotional_angle: 'services_footer',
                  content_category: 'pricing',
                  cta_location: 'services_footer',
                });
              }}
            >
              Get a Financial Plan
            </Link>
          </div>
        </section>

        <footer className="public-linktree-disclosure" role="note">
          <strong>Important disclosures</strong>
          <p>
            Bautista Planning and Analytics, LLC is an registered investment advisor with the State
            of Oregon. This communication is not intended as an offer or solicitation to buy, hold
            or sell any financial instrument or investment advisory services. Planning fees shown on
            this page do not guarantee investment performance, tax savings, or suitability for every
            situation.
          </p>
        </footer>
      </main>
    </div>
  );
}
